/**
 * Prikaz sadržaja reda obaveštenja — tip, trigger, linkovi i akcije moderatora.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  getNotificationTypeLabel,
  getNotificationTriggerHint,
  resolveNotificationDisplay,
} from '@/shared/utils/notificationDisplay';
import { NOTIFICATION_ITEM_UI } from './notificationItemUi';

const GENERIC_HEADER_LINK_LABELS = new Set([
  'Преглед',
  'Обавезе',
  'Подешавања',
  'Админ',
  'Регистар',
  'Извештај',
  'Корисници',
  'Обавештења',
  'Објава',
  'Обавеза',
  'Планови',
  'Објаве',
]);

/** Univerzalna labela dugmeta u header dropdown-u — destinacija, ne naziv entiteta. */
const HEADER_LINK_LABEL_BY_KEY = {
  'obl-profile': 'Обавеза',
  'admin-obl': 'Обавеза',
  'obaveze': 'Обавезе',
  'cso-edit': 'Пријава ОЦД',
  'cso-registry': 'Регистар ОЦД',
  'institution-edit': 'Институција',
  'institutions-registry': 'Регистар институција',
  'admin-notice': 'Објава',
  'admin-notices': 'Објаве',
  consultation: 'Консултација',
  notices: 'Објаве',
  report: 'Извештај',
  settings: 'Подешавања',
  users: 'Корисници',
  admin: 'Админ панел',
  notifications: 'Обавештења',
};

/**
 * @param {{ key?: string, label: string, linkHint?: string }} link
 * @returns {string}
 */
export function getHeaderDropdownLinkLabel(link) {
  const hint = String(link.linkHint || '').trim();
  if (hint) return hint;

  const byKey = HEADER_LINK_LABEL_BY_KEY[String(link.key || '')];
  if (byKey) return byKey;

  const label = String(link.label || '').trim();
  if (GENERIC_HEADER_LINK_LABELS.has(label)) return label;

  return 'Отвори';
}

/** @param {{ key?: string, label: string, linkHint?: string }} link */
function getGridLinkButtonContent(link) {
  const label = String(link.label || '').trim();
  const hint = String(link.linkHint || '').trim();
  if (hint) {
    return {
      primary: hint,
      meta: label && !GENERIC_HEADER_LINK_LABELS.has(label) ? label : null,
    };
  }
  if (GENERIC_HEADER_LINK_LABELS.has(label)) {
    return { primary: 'Отвори', meta: null };
  }
  return { primary: label || 'Отвори', meta: null };
}

export const MARK_READ_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

/**
 * @param {object} props
 * @param {object} props.notification
 * @param {import('./buildNotificationContext').NotificationDisplayContext} props.ctx
 * @param {'header'|'dashboard'|'grid-entity'|'grid-type'} props.variant
 * @param {boolean} [props.isRead]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.busy]
 * @param {string|null} [props.moderatorLoading]
 * @param {() => void} [props.onMarkRead]
 * @param {() => void} [props.onAcceptModeration]
 * @param {() => void} [props.onRejectModeration}
 */
export function NotificationRowContent({
  notification,
  ctx,
  variant,
  isRead = false,
  disabled = false,
  busy = false,
  moderatorLoading = null,
  onMarkRead,
  onAcceptModeration,
  onRejectModeration,
  onLinkNavigate,
}) {
  const type = notification?.notification_type || notification?.type;
  const display = resolveNotificationDisplay(notification, ctx);
  const triggerHint = display.triggerHint || getNotificationTriggerHint(type);

  const handleLinkClick = (e) => {
    e.stopPropagation();
    onLinkNavigate?.();
  };

  if (variant === 'grid-type') {
    return (
      <div className="pp-notif-grid-type-cell">
        <span className="pp-notif-type">
          {!isRead ? <span className="pp-notif-unread-badge">Ново</span> : null}
          <span className="pp-notif-type-label">{display.label || getNotificationTypeLabel(type)}</span>
        </span>
        {triggerHint ? (
          <p className="notification-trigger-hint pp-notif-trigger-hint">{triggerHint}</p>
        ) : null}
      </div>
    );
  }

  if (variant === 'grid-entity') {
    const hasLinks = display.links.length > 0;
    return (
      <div className="pp-notif-entity pp-notif-entity--grid">
        {display.context ? (
          <p className="pp-notif-entity-context">{display.context}</p>
        ) : null}
        {hasLinks ? (
          <div className="pp-notif-entity-cta-row" role="group" aria-label="Акције обавештења">
            {display.links.map((link) => {
              const { primary, meta } = getGridLinkButtonContent(link);
              return (
                <Link
                  key={link.key}
                  to={link.to}
                  className="pp-notif-grid-cta-btn"
                  onClick={handleLinkClick}
                >
                  <span className="pp-notif-grid-cta-btn-text">
                    <span className="pp-notif-grid-cta-btn-label">{primary}</span>
                    {meta ? (
                      <span className="pp-notif-grid-cta-btn-meta">{meta}</span>
                    ) : null}
                  </span>
                  <i className="fas fa-arrow-right pp-notif-grid-cta-btn-icon" aria-hidden />
                </Link>
              );
            })}
          </div>
        ) : null}
        {display.showModeratorActions && !isRead ? (
          <div className="pp-notif-moderator-actions">
            <button
              type="button"
              className="pp-notif-mod-btn pp-notif-mod-btn--accept"
              onClick={onAcceptModeration}
              disabled={disabled || !!moderatorLoading}
              title="Прихвати да будеш moderator"
            >
              {moderatorLoading === 'accept' ? '...' : 'Прихвати'}
            </button>
            <button
              type="button"
              className="pp-notif-mod-btn pp-notif-mod-btn--reject"
              onClick={onRejectModeration}
              disabled={disabled || !!moderatorLoading}
              title="Одбиј улогу moderatora"
            >
              {moderatorLoading === 'reject' ? '...' : 'Одбиј'}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const ui = NOTIFICATION_ITEM_UI[variant === 'dashboard' ? 'dashboard' : 'header'];
  const markReadBusy = busy && !moderatorLoading;
  const isDashboard = variant === 'dashboard';
  const hasLinks = display.links.length > 0;
  const hasModerator = display.showModeratorActions;
  const hasActionsTop = hasLinks || hasModerator;

  const renderMarkReadFooter = () => {
    if (!onMarkRead) return null;
    return (
      <div className={ui.markReadWrap}>
        <button
          type="button"
          className={ui.markReadBtn}
          onClick={onMarkRead}
          disabled={busy || !!moderatorLoading}
          aria-label="Означи обавештење као прочитано"
        >
          <span className={ui.markReadIcon} aria-hidden>
            {MARK_READ_ICON}
          </span>
          <span className={ui.markReadText}>
            {markReadBusy ? 'Чување…' : 'Означи као прочитано'}
          </span>
        </button>
      </div>
    );
  };

  const renderModeratorButtons = () => {
    if (!hasModerator) return null;
    return (
      <div className={ui.actionsModerator} role="group" aria-label="Одговор на улогу модератора">
        <button
          type="button"
          className={ui.cmdAccept}
          onClick={onAcceptModeration}
          disabled={!!moderatorLoading || busy}
        >
          {moderatorLoading === 'accept' ? '…' : 'Прихвати'}
        </button>
        <button
          type="button"
          className={ui.cmdReject}
          onClick={onRejectModeration}
          disabled={!!moderatorLoading || busy}
        >
          {moderatorLoading === 'reject' ? '…' : 'Одбиј'}
        </button>
      </div>
    );
  };

  if (isDashboard) {
    return (
      <>
        <div className={ui.body}>
          {triggerHint ? <p className={ui.trigger}>{triggerHint}</p> : null}
          {display.context ? <p className={ui.context}>{display.context}</p> : null}
        </div>

        {hasActionsTop ? (
          <div className={ui.actionsBar}>
            <div className={`${ui.actionsTop} ${ui.actionsToolbar}`}>
              <div className={ui.actionsCta}>
                {hasLinks
                  ? display.links.map((link) => (
                      <Link
                        key={link.key}
                        to={link.to}
                        className={ui.linkBtn}
                        onClick={handleLinkClick}
                      >
                        {link.label}
                      </Link>
                    ))
                  : null}
              </div>
              {renderModeratorButtons()}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className={ui.body}>
        {triggerHint ? <p className={ui.trigger}>{triggerHint}</p> : null}
        {display.context ? <p className={ui.context}>{display.context}</p> : null}
      </div>

      {hasActionsTop ? (
        <div className={`${ui.actionsTop} ${ui.actionsToolbar}`}>
          <div className={ui.actionsCta}>
            {hasLinks
              ? display.links.map((link) => (
                  <Link
                    key={link.key}
                    to={link.to}
                    className={ui.linkBtn}
                    onClick={handleLinkClick}
                  >
                    <span className={ui.linkBtnLabel}>
                      {getHeaderDropdownLinkLabel(link)}
                    </span>
                    <i
                      className={`fas fa-arrow-right ${ui.linkBtnIcon}`}
                      aria-hidden
                    />
                  </Link>
                ))
              : null}
          </div>
          {renderModeratorButtons()}
        </div>
      ) : null}

      {renderMarkReadFooter()}
    </>
  );
}

export default NotificationRowContent;
